import { useMsal } from '@azure/msal-react'
import { drfFetch, LoadingEllipsis, login, useStore, useRouter } from '@/core'
import { UNION_COMPANIES } from '@/consts'
import { fetchUserInfo } from '@u/drf/auth'
import { legacyRoute } from '@u/legacy-route'
import { CompanyDoesNotExistModal } from '@c/onboarding-app/company-does-not-exist-modal'
import { useTranslation } from 'react-i18next'
import styles from './style.module.styl'
import { IconMicrosoft } from '@c/icon'
import { useState, useEffect } from 'react'

export const MicrosoftButton = () => {
  const { t, i18n } = useTranslation()
  const { instance, inProgress, accounts } = useMsal()
  const store = useStore()
  const router = useRouter()
  const [modal, setModal] = store.use(['modal'])
  const [next] = store.use(['next'])
  const [avatar, setAvatar] = store.use(['sso', 'avatar'], '')
  const [language, setLanguage] = store.use(['language'])
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [failedAttempts, setFailedAttempts] = useState(0)

  useEffect(() => {
    const handleRedirectResponse = async () => {
      // Don't try to handle redirect if MSAL is still initializing
      if (inProgress !== 'none') {
        console.log('MSAL: Authentication in progress, waiting...')
        return
      }

      // Check if we're returning from a redirect
      if (sessionStorage.getItem('msalRedirectInProgress') === 'true') {
        try {
          const response = await instance.handleRedirectPromise()

          if (response) {
            setIsLoggingIn(true)
            sessionStorage.removeItem('msalRedirectInProgress')

            // Extract user information
            const profile = response.account
            const email =
              profile?.username || profile?.idTokenClaims?.preferred_username
            const accessToken = response.idToken
            const microsoftTenantId =
              profile?.tenantId || profile?.idTokenClaims?.tid

            // Complete the login process with your backend
            try {
              const loginRes = await drfFetch('microsoftLogin', {
                method: 'POST',
                body: {
                  email,
                  accessToken,
                  microsoftTenantId,
                  isNonprofitAccount: false,
                },
              })

              if (loginRes?.avatarContent) {
                setAvatar(loginRes.avatarContent)
              }

              // Handle special case for union companies
              if (
                loginRes.canVolunteerDuringBusinessHours === null &&
                UNION_COMPANIES.some(c => c.toLowerCase() === loginRes.companyName?.toLowerCase())
              ) {
                router.push({ name: 'confirmEmployeeType' })
                setIsLoggingIn(false)
                return
              }

              const info = await fetchUserInfo({
                ...loginRes,
                token: loginRes.token,
              })
              const preferredLanguage = info?.preferredLanguage || language
              setLanguage(preferredLanguage)

              login(store, {
                token: loginRes.token,
                info,
                tokenTimestamp: new Date().getTime(),
              })

              // Navigate after successful login
              setTimeout(() => {
                if (next.route) {
                  router.push(next.route)
                } else if (next.string) {
                  legacyRoute(next.string)
                } else if (info.legacyNextUrl) {
                  legacyRoute(info.legacyNextUrl)
                } else if (info.nextUrl) {
                  router.push({ name: info.nextUrl })
                } else if (info.legacyHomeUrl) {
                  legacyRoute(info.legacyHomeUrl)
                } else {
                  router.push({ name: info.homeUrlName })
                }

                setIsLoggingIn(false)
              }, 200)
            } catch (err) {
              console.error('MSAL: Backend authentication failed:', err)

              if (err.fieldErrors?.email?.includes('Company does not exist')) {
                setModal('company-does-not-exist')
              }

              setIsLoggingIn(false)
            }
          } else {
            console.log('MSAL: No response data from redirect')
            sessionStorage.removeItem('msalRedirectInProgress')
          }
        } catch (err) {
          console.error('MSAL: Error handling redirect:', err)
          sessionStorage.removeItem('msalRedirectInProgress')
          setIsLoggingIn(false)
        }
      }

      const urlParams = new URLSearchParams(window.location.search)
      const errorDesc = urlParams.get('error_description')
      if (errorDesc) {
        console.error('MSAL: Error in redirect URL:', errorDesc)
        setIsLoggingIn(false)
      }
    }

    // Only attempt to handle redirect when MSAL is ready
    handleRedirectResponse()
  }, [inProgress]) // Add inProgress as a dependency

  // Azure Best Practice: Modified to always use redirect flow
  const handleMicrosoftLogin = async () => {
    try {
      // Clear existing accounts first (if any)
      if (accounts && accounts.length > 0) {
        await instance.clearCache()
      }

      // Azure Best Practice: Use simpler configuration
      const authConfig = {
        scopes: [
          'openid',
          'profile',
          'email',
          'https://graph.microsoft.com/User.Read',
        ],
        redirectUri: window.location.origin,
        prompt: 'select_account',
      }

      // Set flag in sessionStorage to detect return from redirect
      sessionStorage.setItem('msalRedirectInProgress', 'true')

      // Store current URL to return after redirect
      sessionStorage.setItem('msalPreRedirectPath', window.location.pathname)

      // Azure Best Practice: Use loginRedirect for more reliable auth flow
      console.log('MSAL: Initiating redirect flow')
      await instance.loginRedirect(authConfig)

      // Page will navigate away, so we don't return anything
      return null
    } catch (error) {
      console.error('MSAL: Redirect login setup error: ', error)

      console.error('MSAL: Error details:', {
        name: error.name,
        message: error.message,
        errorCode: error.errorCode,
        errorMessage: error.errorMessage,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      })

      // Clear the flag in case of error
      sessionStorage.removeItem('msalRedirectInProgress')
      return null
    }
  }

  const handlePurposelyLogin = async () => {
    if (isLoggingIn) {
      return
    }

    setIsLoggingIn(true)

    try {
      // Simply start the redirect flow - will navigate away from the page
      await handleMicrosoftLogin()
      // We won't reach this point in normal flow since page redirects
    } catch (error) {
      console.error('MSAL: Unexpected error during login flow:', error)
      setIsLoggingIn(false)
    }
  }

  return (
    <>
      <CompanyDoesNotExistModal />
      <button
        className={styles.microsoft}
        onClick={handlePurposelyLogin}
        disabled={isLoggingIn || inProgress !== 'none'}
      >
        <div className={styles.microsoftIconWrapper}>
          <IconMicrosoft />
        </div>
        <span>
          {isLoggingIn ? 'Signing in...' : t('auth.microsoftButton.text')}
        </span>
      </button>
    </>
  )
}
